import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { logWarning } from '@/services/errorService'

import type { LocationData, MenuSchedule, OrderType } from '@/shared/api/location'
import {
	calculateCart,
	type CalculateCartItemModifier,
	type CalculateCartRequest,
} from '@/shared/api/cart'
import { ApiError, CartApiError } from '@/lib/apiErrors'
import {
	CartItemSchema,
	type CartItem,
	type CartItemModifier,
	type CalculationDomainError,
	type CalculationResponse,
} from '@/shared/types/cart'
import type { AppDispatch, RootState } from '@/store'

// Validates raw localStorage items against the CartItemSchema — filters out any
// malformed entries (stale data, manual edits) rather than crashing or loading corrupt state.
function parseStoredItems(raw: unknown): CartItem[] {
	if (!Array.isArray(raw)) return []
	return raw.flatMap((entry, index) => {
		const result = CartItemSchema.safeParse(entry)
		if (!result.success) {
			logWarning('[ticketSlice] Skipping malformed cart item from localStorage', { index })
			return []
		}
		return [result.data]
	})
}

const LOCAL_STORAGE_TTL_MS = 7_200_000 // 2 hours — matches Ember set-local-storage.js

// ---------------------------------------------------------------------------
// Local storage shape
// ---------------------------------------------------------------------------

interface StoredTicketData {
	items?: CartItem[]
	orderTypeGuid?: string
	menuScheduleRef?: string
	dineInTableName?: string
	isDeferred?: boolean
	expectedTimeUtc?: string
	orderTimeUtc?: string
	customerSelectedDay?: string
	dropOffPointRef?: string | null
	dropOffPointLastMileValue?: string | null
	deliveryAddress?: DeliveryAddress | null
	expiresAt?: number
}

export interface DeliveryAddress {
	addressLine1: string
	addressLine2?: string
	city: string
	state?: string
	zip: string
}

export interface TicketState {
	// --- Order settings (STO-3073) ---
	currentRef: string | null
	selectedOrderTypeGuid: string | null
	selectedOrderType: OrderType | null
	selectedMenuSchedule: MenuSchedule | null
	dineInTableName: string | null
	isDeferred: boolean
	expectedTimeUtc: string | null
	orderTimeUtc: string | null
	customerSelectedDay: string | null
	dropOffPointRef: string | null
	dropOffPointLastMileValue: string | null
	deliveryAddress: DeliveryAddress | null
	referralGuid: string | null
	referralSource: string | null
	rwgToken: string | null
	pendingReorder: { itemGuid: string; itemRef: string } | null
	storeSettingsChangedError: string | null

	// --- Cart items (STO-3130) ---
	// Guards persistToLocalStorage — only true after restoreCart completes.
	// Prevents setOrderType dispatches from StartOrder (which fire before restoreCart)
	// from overwriting a valid stored cart with an empty items array.
	cartRestored: boolean
	items: CartItem[]
	isCalculating: boolean
	calculations: CalculationResponse | null
	calculateErrors: CalculationDomainError[] | null
	// Notification signal: UI reads this, shows a toast, then dispatches clearItemsRemovedCount.
	// Do not auto-clear with setTimeout inside the thunk — the UI component owns the timer.
	itemsRemovedCount: number
	// Refs of items flagged ITMOOSTK by the calculate API. UI navigates to sold-out warning page when non-empty.
	itemsOutOfStock: string[]
	// Ember parity: dineInAllowDuringOffHours from LocationData.
	// When false, dine-in checkout is blocked while duringOffHours is true on the selected schedule.
	dineInAllowDuringOffHours: boolean

	// True after checkout is blocked for an order-type minimum — drives warning/success alert visibility.
	// Reset when order type changes or when the user drops back below the minimum.
	showOrderTypeMinWarning: boolean
	// Bumped each time checkout is retried while the warning is already showing — triggers nudge animation.
	orderTypeMinAlertNudge: number

	// --- Tip state (STO-3130 — state only; wired into calculate request in P2-S3-09) ---
	selectedTip: 'percent' | 'custom' | null
	tipPercentage: number | null
	tipCustomAmount: number | null

	// P2-S5-04 fields go here:
	//   discountCode, giftCardAppliedAmount, couponCode, tenderTypeGuid,
	//   discountCodeError, discountCodeValid, giftCardNumber, giftCardBalance
}

const initialState: TicketState = {
	currentRef: null,
	selectedOrderTypeGuid: null,
	selectedOrderType: null,
	selectedMenuSchedule: null,
	dineInTableName: null,
	isDeferred: false,
	expectedTimeUtc: null,
	orderTimeUtc: null,
	customerSelectedDay: null,
	dropOffPointRef: null,
	dropOffPointLastMileValue: null,
	deliveryAddress: null,
	referralGuid: null,
	referralSource: null,
	rwgToken: null,
	pendingReorder: null,
	storeSettingsChangedError: null,
	cartRestored: false,
	items: [],
	isCalculating: false,
	calculations: null,
	calculateErrors: null,
	itemsRemovedCount: 0,
	itemsOutOfStock: [],
	dineInAllowDuringOffHours: false,
	showOrderTypeMinWarning: false,
	orderTypeMinAlertNudge: 0,
	selectedTip: null,
	tipPercentage: null,
	tipCustomAmount: null,
}

// ---------------------------------------------------------------------------
// localStorage persistence (order settings only)
// ---------------------------------------------------------------------------

function persistToLocalStorage(state: TicketState): void {
	if (!state.currentRef) return
	// Do not write until restoreCart has completed — any dispatch before restore
	// would overwrite the stored entry and lose items that haven't loaded yet.
	if (!state.cartRestored) return
	const data: StoredTicketData = {}
	if (state.items.length > 0) data.items = state.items
	if (state.selectedOrderTypeGuid) data.orderTypeGuid = state.selectedOrderTypeGuid
	if (state.selectedMenuSchedule) data.menuScheduleRef = state.selectedMenuSchedule.ref
	if (state.dineInTableName) data.dineInTableName = state.dineInTableName
	data.isDeferred = state.isDeferred
	if (state.expectedTimeUtc) data.expectedTimeUtc = state.expectedTimeUtc
	if (state.orderTimeUtc) data.orderTimeUtc = state.orderTimeUtc
	if (state.customerSelectedDay) data.customerSelectedDay = state.customerSelectedDay
	if (state.dropOffPointRef) data.dropOffPointRef = state.dropOffPointRef
	if (state.dropOffPointLastMileValue)
		data.dropOffPointLastMileValue = state.dropOffPointLastMileValue
	if (state.deliveryAddress) data.deliveryAddress = state.deliveryAddress
	// Write current items as-is. An empty array here means the user has intentionally
	// cleared the cart (delete, clearCart, ITMNA recovery) — do not preserve stale
	// localStorage items. cartRestored is already verified above, so this is never
	// called during the bootstrap race where items haven't loaded yet.
	if (state.items.length > 0) {
		data.items = state.items
	}
	data.expiresAt = Date.now() + LOCAL_STORAGE_TTL_MS
	try {
		localStorage.setItem(state.currentRef, JSON.stringify(data))
	} catch (error) {
		logWarning('[ticketSlice] Failed to persist cart to localStorage', { error })
	}
}

// ---------------------------------------------------------------------------
// restoreCart async thunk (STO-3073)
// ---------------------------------------------------------------------------

export const restoreCart = createAsyncThunk(
	'ticket/restoreCart',
	({ ref, location }: { ref: string; location: LocationData }) => {
		const raw = localStorage.getItem(ref)
		if (!raw) return null

		let stored: StoredTicketData
		let outerItems: CartItem[] | undefined
		try {
			const parsed = JSON.parse(raw) as unknown
			// Support Ember format { items: [], orderSettings: {...}, expiresAt: ... }
			if (typeof parsed === 'object' && parsed !== null && 'orderSettings' in parsed) {
				const wrapped = parsed as {
					orderSettings: StoredTicketData
					items?: CartItem[]
					expiresAt?: number
				}
				stored = wrapped.orderSettings
				if (wrapped.expiresAt !== undefined) stored.expiresAt = wrapped.expiresAt
				outerItems = Array.isArray(wrapped.items) ? wrapped.items : undefined
			} else {
				stored = parsed as StoredTicketData
			}
		} catch (error) {
			logWarning('[ticketSlice] Malformed localStorage data for ref, resetting cart', {
				ref,
				error,
			})
			return null
		}

		if (stored.expiresAt !== undefined && stored.expiresAt < Date.now()) {
			localStorage.removeItem(ref)
			return null
		}

		const result: Partial<TicketState> = {}

		if (stored.orderTypeGuid) {
			const match = location.orderTypes.find((ot) => ot.ref === stored.orderTypeGuid)
			if (match) {
				result.selectedOrderTypeGuid = match.ref
				result.selectedOrderType = match
			} else {
				result.selectedOrderTypeGuid = null
				result.selectedOrderType = null
				result.storeSettingsChangedError = 'error:orderType:unavailable'
			}
		}

		if (stored.menuScheduleRef) {
			const match = location.menuSchedules.find((s) => s.ref === stored.menuScheduleRef)
			if (match) {
				result.selectedMenuSchedule = match
			} else {
				result.selectedMenuSchedule = null
			}
		}

		if (stored.dineInTableName) result.dineInTableName = stored.dineInTableName
		if (stored.isDeferred !== undefined) result.isDeferred = stored.isDeferred
		if (stored.expectedTimeUtc) result.expectedTimeUtc = stored.expectedTimeUtc
		if (stored.orderTimeUtc) {
			result.orderTimeUtc = stored.orderTimeUtc
		} else if (result.isDeferred) {
			// Legacy/truncated data: orderTimeUtc absent for a deferred state.
			// Reset to ASAP so the availability query is not silently skipped.
			result.isDeferred = false
			result.expectedTimeUtc = null
			result.customerSelectedDay = null
		}
		if (stored.customerSelectedDay) result.customerSelectedDay = stored.customerSelectedDay
		if (stored.dropOffPointRef) result.dropOffPointRef = stored.dropOffPointRef
		if (stored.dropOffPointLastMileValue)
			result.dropOffPointLastMileValue = stored.dropOffPointLastMileValue
		if (stored.deliveryAddress) result.deliveryAddress = stored.deliveryAddress

		// Restore items — always set (even to []) to prevent location bleed.
		// Wrapped format stores items at the top level; flat format stores them inside StoredTicketData.
		// parseStoredItems validates each entry against CartItemSchema — malformed entries are
		// dropped with a warning rather than crashing or loading corrupt state.
		const rawItems = outerItems ?? stored.items
		result.items = parseStoredItems(rawItems).filter((item) => item.locationRef === ref)

		return result
	},
)

// ---------------------------------------------------------------------------
// buildCalculateRequest — pure helper, tested independently from the thunk
// ---------------------------------------------------------------------------

function serializeModifiers(modifiers: CartItemModifier[]): CalculateCartItemModifier[] {
	return modifiers.map((mod) => ({
		modifierSetRef: mod.modifierSetRef,
		modifierRef: mod.modifierRef,
		quantity: mod.quantity,
		modifiers: serializeModifiers(mod.modifiers), // recursive — handles unbounded nesting depth
	}))
}

export function buildCalculateRequest(state: TicketState): CalculateCartRequest {
	if (!state.selectedOrderTypeGuid) {
		throw new Error(
			'[buildCalculateRequest] selectedOrderTypeGuid must be set — call from the calculate thunk only after Step 1 guard',
		)
	}
	return {
		items: state.items.map((item) => ({
			ref: item.id, // client UUID — Ember sends this as 'ref' (primaryKey: 'ref' in application.js serializer)
			locationRef: item.locationRef,
			itemRef: item.itemRef,
			quantity: item.quantity,
			specialRequest: item.specialRequest,
			modifiers: serializeModifiers(item.modifiers),
		})),
		orderTypeRef: state.selectedOrderTypeGuid,
		menuScheduleRef: state.selectedMenuSchedule?.ref ?? null,
		isAtCheckout: false, // TODO: P2-S3-10 — wire to validateOutOfStockItems param
		discounts: [], // Ember always sends discounts: [] even when empty; P2-S5-04 expands this to loyalty/coupon
		// TODO: P2-S3-09 — add tips from selectedTip / tipPercentage / tipCustomAmount
	}
}

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const ticketSlice = createSlice({
	name: 'ticket',
	initialState,
	reducers: {
		// --- Order settings (STO-3073 / STO-3095) ---

		// guid must equal orderType.ref — downstream selectors rely on this invariant
		setOrderType(state, action: PayloadAction<{ guid: string; orderType: OrderType }>) {
			state.selectedOrderTypeGuid = action.payload.guid
			state.selectedOrderType = action.payload.orderType
			// Reset min-amount alert state when order type changes — new order type has its own limits.
			state.showOrderTypeMinWarning = false
			state.orderTypeMinAlertNudge = 0
			persistToLocalStorage(state)
		},

		setTable(state, action: PayloadAction<string>) {
			state.dineInTableName = action.payload
			persistToLocalStorage(state)
		},

		setSchedule(
			state,
			action: PayloadAction<{
				isDeferred: boolean
				expectedTimeUtc?: string | null
				orderTimeUtc?: string | null
				customerSelectedDay?: string | null
			}>,
		) {
			state.isDeferred = action.payload.isDeferred
			if (action.payload.isDeferred) {
				state.expectedTimeUtc = action.payload.expectedTimeUtc ?? null
				state.orderTimeUtc = action.payload.orderTimeUtc ?? null
				state.customerSelectedDay = action.payload.customerSelectedDay ?? null
			} else {
				state.expectedTimeUtc = null
				state.orderTimeUtc = null
				state.customerSelectedDay = null
			}
			persistToLocalStorage(state)
		},

		setReferral(state, action: PayloadAction<{ guid: string; source: string }>) {
			state.referralGuid = action.payload.guid
			state.referralSource = action.payload.source
		},

		setRwgToken(state, action: PayloadAction<string>) {
			state.rwgToken = action.payload
		},

		setPendingReorder(state, action: PayloadAction<{ itemGuid: string; itemRef: string }>) {
			state.pendingReorder = action.payload
		},

		clearStoreSettingsError(state) {
			state.storeSettingsChangedError = null
		},

		setDineInAllowDuringOffHours(state, action: PayloadAction<boolean>) {
			state.dineInAllowDuringOffHours = action.payload
		},

		setCurrentRef(state, action: PayloadAction<string>) {
			state.currentRef = action.payload
		},

		setMenuSchedule(state, action: PayloadAction<MenuSchedule>) {
			state.selectedMenuSchedule = action.payload
			persistToLocalStorage(state)
		},

		setDeliveryAddress(state, action: PayloadAction<DeliveryAddress>) {
			state.deliveryAddress = action.payload
			state.dropOffPointRef = null
			state.dropOffPointLastMileValue = null
			persistToLocalStorage(state)
		},

		setDropOffPoint(
			state,
			action: PayloadAction<{
				dropOffPointRef: string
				dropOffPointLastMileValue: string | null
			}>,
		) {
			state.dropOffPointRef = action.payload.dropOffPointRef
			state.dropOffPointLastMileValue = action.payload.dropOffPointLastMileValue
			state.deliveryAddress = null
			persistToLocalStorage(state)
		},

		// --- Cart actions (STO-3130) ---

		// Caller provides all CartItem fields except id — the store generates the UUID.
		// This ensures id is always a proper UUID and callers cannot inject arbitrary ids.
		addItem(state, action: PayloadAction<Omit<CartItem, 'id'>>) {
			state.items.push({ ...action.payload, id: crypto.randomUUID() })
			persistToLocalStorage(state)
		},

		// Removes by cart item UUID. No-op if the id is not found.
		removeItem(state, action: PayloadAction<string>) {
			state.items = state.items.filter((item) => item.id !== action.payload)
			persistToLocalStorage(state)
		},

		// Removes all items matching a menu item ref (itemRef).
		// Also removes orphaned items where itemRef is empty — mirrors Ember's
		// !cartItem.get('item.id') guard in ticket.js:374.
		// Used exclusively by the calculate thunk's ITMNA recovery path.
		removeItemsByMenuRef(state, action: PayloadAction<string>) {
			state.items = state.items.filter(
				(item) => item.itemRef !== action.payload && !!item.itemRef,
			)
			persistToLocalStorage(state)
		},

		// Replaces the matching item in-place. Array length is unchanged.
		// No-op if the id is not found. Used by the edit item flow.
		updateItem(state, action: PayloadAction<CartItem>) {
			const index = state.items.findIndex((item) => item.id === action.payload.id)
			if (index !== -1) {
				state.items[index] = action.payload
				persistToLocalStorage(state)
			}
		},

		// Updates quantity only — all other fields unchanged.
		updateItemQuantity(state, action: PayloadAction<{ itemId: string; quantity: number }>) {
			const item = state.items.find((i) => i.id === action.payload.itemId)
			if (item) {
				item.quantity = action.payload.quantity
				persistToLocalStorage(state)
			}
		},

		// --- Tip actions (STO-3130 — state only; wired into calculate request in P2-S3-09) ---

		setTip(
			state,
			action: PayloadAction<
				{ type: 'percent'; percentage: number } | { type: 'custom'; amount: number }
			>,
		) {
			if (action.payload.type === 'percent') {
				state.selectedTip = 'percent'
				state.tipPercentage = action.payload.percentage
				state.tipCustomAmount = null
			} else {
				state.selectedTip = 'custom'
				state.tipCustomAmount = action.payload.amount
				state.tipPercentage = null
			}
			// TODO: P2-S3-09 — persist tip selection here
		},

		clearTip(state) {
			state.selectedTip = null
			state.tipPercentage = null
			state.tipCustomAmount = null
		},

		// Called by the cart UI after displaying the "items removed" notification toast.
		// The UI component owns the dismiss timer — do not auto-clear here.
		clearItemsRemovedCount(state) {
			state.itemsRemovedCount = 0
		},

		// Set by the calculate thunk when ITMOOSTK errors are returned (flag ON).
		// UI navigates to the sold-out warning page; page reads this to list affected items.
		setItemsOutOfStock(state, action: PayloadAction<string[]>) {
			state.itemsOutOfStock = action.payload
		},

		// Called by SoldOutWarningView after the user confirms removal.
		clearItemsOutOfStock(state) {
			state.itemsOutOfStock = []
		},

		// Called by CartActionsFooter when checkout is blocked by an unmet minimum.
		// If the warning was already showing, bumps the nudge counter (triggers shake animation).
		// If not yet showing, sets the flag so the alert becomes visible.
		triggerOrderTypeMinWarning(state) {
			if (state.showOrderTypeMinWarning) {
				state.orderTypeMinAlertNudge += 1
			} else {
				state.showOrderTypeMinWarning = true
			}
		},

		// Called when the cart total drops back below the minimum (from items being removed).
		// Resets both alert visibility and nudge count.
		resetOrderTypeMinWarning(state) {
			state.showOrderTypeMinWarning = false
			state.orderTypeMinAlertNudge = 0
		},

		// Clears the full cart and all derived state.
		// Ember parity: ticket.js clearCart() called calculate() after clearing, which
		// immediately hit the early exit (items.length === 0). In React the thunk's
		// early exit achieves the same result — we do not dispatch calculate() from here.
		clearCart(state) {
			if (state.currentRef) {
				try {
					localStorage.removeItem(state.currentRef)
				} catch (error) {
					logWarning('[ticketSlice] Failed to remove cart from localStorage', { error })
				}
			}
			state.items = []
			state.isCalculating = false
			state.calculations = null
			state.calculateErrors = null
			state.itemsRemovedCount = 0
			state.itemsOutOfStock = []
			state.showOrderTypeMinWarning = false
			state.orderTypeMinAlertNudge = 0
			state.selectedTip = null
			state.tipPercentage = null
			state.tipCustomAmount = null
		},

		// --- Internal actions used only by the calculate thunk ---

		setIsCalculating(state, action: PayloadAction<boolean>) {
			state.isCalculating = action.payload
		},

		setCalculations(state, action: PayloadAction<CalculationResponse | null>) {
			state.calculations = action.payload
		},

		setCalculateErrors(state, action: PayloadAction<CalculationDomainError[] | null>) {
			state.calculateErrors = action.payload
		},

		setItemsRemovedCount(state, action: PayloadAction<number>) {
			state.itemsRemovedCount = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(restoreCart.fulfilled, (state, action) => {
			state.currentRef = action.meta.arg.ref
			state.cartRestored = true
			if (!action.payload) return
			Object.assign(state, action.payload)
		})
	},
})

// ---------------------------------------------------------------------------
// calculate plain thunk
//
// Plain thunk (not createAsyncThunk) because the ITMNA recovery path needs to
// dispatch actions and re-dispatch calculate() in a controlled sequence.
// createAsyncThunk's pending/fulfilled/rejected lifecycle makes that awkward.
//
// Ember equivalent: ticket.js calculate() method (lines 309–419).
// ---------------------------------------------------------------------------

export const calculate =
	(args?: { quantityOnHandEnabled?: boolean }) =>
	async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
		const state = getState()
		const ticket = state.ticket

		// Step 1 — Early exit: mirrors Ember ticket.js lines 310–312.
		// No network call when cart is empty, order type is not set, or location not yet bootstrapped.
		if (ticket.items.length === 0 || !ticket.selectedOrderTypeGuid || !ticket.currentRef) {
			dispatch(ticketSlice.actions.setCalculations(null))
			dispatch(ticketSlice.actions.setCalculateErrors(null))
			return
		}

		// Step 2 — Inflight guard: prevents parallel calculate calls.
		if (ticket.isCalculating) return

		// Step 3 — Mark as in-flight.
		dispatch(ticketSlice.actions.setIsCalculating(true))

		try {
			// Step 4 — Build request body.
			const body = buildCalculateRequest(ticket)

			// Step 5 — Call API. calculateCart is mocked in tests; implemented in P2-S3-02.

			const response = await calculateCart(ticket.currentRef, body)

			// Step 6 — Success.
			dispatch(ticketSlice.actions.setCalculations(response))
			dispatch(ticketSlice.actions.setCalculateErrors(null))
			dispatch(ticketSlice.actions.setIsCalculating(false))
		} catch (err) {
			if (err instanceof CartApiError) {
				// Step 7 — ITMNA recovery: items no longer available on the menu.
				// Collect unique menu item refs from all ITMNA errors.
				const itmnaRefs = [
					...new Set(
						err.errors
							.filter((e) => e.code === 'ITMNA')
							.map((e) => {
								const ref = e.metadata?.InvalidItemRef
								return typeof ref === 'string' ? ref : null
							})
							.filter((ref): ref is string => ref !== null && ref.length > 0),
					),
				]

				if (itmnaRefs.length > 0) {
					const countBefore = getState().ticket.items.length
					itmnaRefs.forEach((ref) =>
						dispatch(ticketSlice.actions.removeItemsByMenuRef(ref)),
					)
					const countAfter = getState().ticket.items.length

					if (countAfter < countBefore) {
						// Items were actually removed — retry makes progress.
						dispatch(ticketSlice.actions.setItemsRemovedCount(countBefore - countAfter))
						// Critical: reset isCalculating BEFORE re-dispatching calculate.
						// If isCalculating remains true, the inflight guard in Step 2 silently
						// blocks the retry and the cart never recovers.
						// Pass args through so quantityOnHandEnabled is preserved — without it,
						// an ITMOOSTK response on the retry call silently falls to Step 9.
						dispatch(ticketSlice.actions.setIsCalculating(false))
						await dispatch(calculate(args))
						return // do not fall through to Step 9
					}
					// Nothing removed — ITMNA ref did not match any cart item.
					// Fall through to Step 9 rather than retrying with an identical request.
				}

				// Step 8 — Deferred error branches.

				// ITMOOSTK — only runs if no ITMNA errors were present (ITMNA returned early above).
				// Flag OFF → silently ignored; calculate resolves normally and checkout proceeds.
				const itmoostkRefs = [
					...new Set(
						err.errors
							.filter((e) => e.code === 'ITMOOSTK')
							.map((e) => {
								const ref = e.metadata?.InvalidItemRef
								return typeof ref === 'string' ? ref : null
							})
							.filter((ref): ref is string => ref !== null && ref.length > 0),
					),
				]
				if (args?.quantityOnHandEnabled && itmoostkRefs.length > 0) {
					dispatch(ticketSlice.actions.setItemsOutOfStock(itmoostkRefs))
					dispatch(ticketSlice.actions.setIsCalculating(false))
					return Promise.reject(new Error('ITMOOSTK'))
				}

				// TODO: P2-S5-04 — ORDCOUPINV: clear discountCode, set discountCodeError, re-calculate
				// TODO: P2-S5-04 — ORDCOUPINSTOT: set discountCodeError (insufficient total)
			}

			// Step 9 — Non-recoverable: unknown domain errors, network failures, or schema errors.
			// ApiError with SCHEMA_ERROR arrives here when the server returns an unexpected shape —
			// surface it as a cart error so the UI shows a failure state rather than null (clean cart).
			let calculateErrors: CalculationDomainError[] | null = null
			if (err instanceof CartApiError) {
				calculateErrors = err.errors
			} else if (err instanceof ApiError) {
				calculateErrors = [{ code: err.code }]
			}
			dispatch(ticketSlice.actions.setCalculateErrors(calculateErrors))
			dispatch(ticketSlice.actions.setCalculations(null))
			dispatch(ticketSlice.actions.setIsCalculating(false))
		}

		// Step 10 — Post-calculate side-effects from ticket.js lines 395–418.
		// Runs after Step 6 (success) AND after Step 9 (non-recoverable error fall-through).
		// Does NOT run on early-exit (Step 1) or ITMNA recovery (early return above).
		// IMPORTANT: P2-S5-04 must gate each operation on !state.ticket.calculateErrors —
		// gift card redemption and coupon validation must not execute after an API failure.
		// TODO: P2-S5-04 — if giftCardAppliedAmount, call redeemGiftCardAmount(amount)  [ticket.js:396–399]
		// TODO: P2-S5-04 — if discountCode, validate discountCodeValid; set discountCodeError if not found  [ticket.js:401–408]
		// TODO: P2-S5-04 — auto-select CASH tender if isCustomCashPayment (dueAmount===0, no gift card)  [ticket.js:410–416]
	}

// ---------------------------------------------------------------------------
// Action exports
// ---------------------------------------------------------------------------

export const {
	setOrderType,
	setTable,
	setSchedule,
	setReferral,
	setRwgToken,
	setPendingReorder,
	clearStoreSettingsError,
	setDineInAllowDuringOffHours,
	setCurrentRef,
	setMenuSchedule,
	setDeliveryAddress,
	setDropOffPoint,
	addItem,
	removeItem,
	removeItemsByMenuRef,
	updateItem,
	updateItemQuantity,
	setTip,
	clearTip,
	clearItemsRemovedCount,
	setItemsOutOfStock,
	clearItemsOutOfStock,
	triggerOrderTypeMinWarning,
	resetOrderTypeMinWarning,
	clearCart,
} = ticketSlice.actions

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

// --- Order settings selectors (STO-3073) ---
export const selectSelectedOrderTypeGuid = (state: { ticket: TicketState }) =>
	state.ticket.selectedOrderTypeGuid
export const selectSelectedOrderType = (state: { ticket: TicketState }) =>
	state.ticket.selectedOrderType
export const selectIsDeferred = (state: { ticket: TicketState }) => state.ticket.isDeferred
export const selectExpectedTimeUtc = (state: { ticket: TicketState }) =>
	state.ticket.expectedTimeUtc
export const selectOrderTimeUtc = (state: { ticket: TicketState }) => state.ticket.orderTimeUtc
export const selectCustomerSelectedDay = (state: { ticket: TicketState }) =>
	state.ticket.customerSelectedDay
export const selectDineInTableName = (state: { ticket: TicketState }) =>
	state.ticket.dineInTableName
export const selectStoreSettingsChangedError = (state: { ticket: TicketState }) =>
	state.ticket.storeSettingsChangedError
export const selectSelectedMenuSchedule = (state: { ticket: TicketState }) =>
	state.ticket.selectedMenuSchedule
export const selectDropOffPointRef = (state: { ticket: TicketState }) =>
	state.ticket.dropOffPointRef
export const selectDropOffPointLastMileValue = (state: { ticket: TicketState }) =>
	state.ticket.dropOffPointLastMileValue
export const selectDeliveryAddress = (state: { ticket: TicketState }) =>
	state.ticket.deliveryAddress

// --- Cart selectors (STO-3130) ---
export const selectCartItems = (state: { ticket: TicketState }) => state.ticket.items
export const selectIsCalculating = (state: { ticket: TicketState }) => state.ticket.isCalculating
export const selectCalculations = (state: { ticket: TicketState }) => state.ticket.calculations
export const selectCalculateErrors = (state: { ticket: TicketState }) =>
	state.ticket.calculateErrors
export const selectItemsRemovedCount = (state: { ticket: TicketState }) =>
	state.ticket.itemsRemovedCount
export const selectItemsOutOfStock = (state: { ticket: TicketState }) =>
	state.ticket.itemsOutOfStock

// --- Tip selectors (STO-3130) ---
export const selectSelectedTip = (state: { ticket: TicketState }) => state.ticket.selectedTip
export const selectTipPercentage = (state: { ticket: TicketState }) => state.ticket.tipPercentage
export const selectTipCustomAmount = (state: { ticket: TicketState }) =>
	state.ticket.tipCustomAmount

// Memoised — iterates items[] to sum quantities.
// Returns total item count (sum of quantities, not array length).
// e.g. 3 items of quantity 2 each → 6, not 3.
// Used by MenuHeader cart badge (STO-3099).
export const selectCartItemCount = createSelector(
	(state: { ticket: TicketState }) => state.ticket.items,
	(items) => items.reduce((sum, item) => sum + item.quantity, 0),
)

// Memoised — reads server-confirmed total from nested calculations response.
// Returns 0 when no calculation has run yet (safe default for cart display).
export const selectCartTotal = createSelector(
	(state: { ticket: TicketState }) => state.ticket.calculations,
	(calculations) => calculations?.totals.grandTotal ?? 0,
)

export const selectShowOrderTypeMinWarning = (state: { ticket: TicketState }) =>
	state.ticket.showOrderTypeMinWarning
export const selectOrderTypeMinAlertNudge = (state: { ticket: TicketState }) =>
	state.ticket.orderTypeMinAlertNudge

// Ember parity: menu-schedule.js isAvailable + checkout-button.js hasNoOffHoursRestrictions.
//
// Two distinct checks combined:
// 1. Schedule hours check: current time must fall within at least one schedule hour window.
//    Dine-in orders skip this check entirely (Ember: dineIn || hours.any(h => h.isAvailable)).
// 2. Off-hours restriction: when dine-in AND the schedule is marked duringOffHours AND the
//    venue has NOT enabled dineInAllowDuringOffHours, checkout is blocked.
//    (Ember: !dineIn || !duringOffHours || dineInAllowDuringOffHours)
//
// When no schedule is selected the store is treated as available — order settings
// would have blocked upstream if no valid schedule existed.
export const selectIsScheduleAvailable = createSelector(
	(state: { ticket: TicketState }) => state.ticket.selectedMenuSchedule,
	(state: { ticket: TicketState }) => state.ticket.dineInTableName,
	(state: { ticket: TicketState }) => state.ticket.dineInAllowDuringOffHours,
	(schedule, dineInTableName, dineInAllowDuringOffHours): boolean => {
		const isDineIn = !!dineInTableName

		if (!schedule) return true // no schedule selected — treat as available

		// Off-hours restriction (Ember: hasNoOffHoursRestrictions)
		// Block dine-in checkout when the schedule is in off-hours and the venue disallows it.
		if (isDineIn && schedule.duringOffHours && !dineInAllowDuringOffHours) return false

		// Schedule hours check (Ember: menuSchedule.isAvailable)
		// Dine-in bypasses this — the table is always accessible regardless of schedule hours.
		if (isDineIn) return true
		const now = new Date()
		return schedule.hours.some(
			(hour) => now > new Date(hour.startDateTime) && now < new Date(hour.endDateTime),
		)
	},
)

// Derived selector — computes whether the cart total meets the selected order type's
// min/max constraints. All amounts in cents. Returns null when no constraint applies.
// variant: 'below-min' | 'min-reached' | 'above-max'
export type OrderTypeAmountAlertVariant = 'below-min' | 'min-reached' | 'above-max'
export interface OrderTypeAmountAlert {
	variant: OrderTypeAmountAlertVariant
	orderTypeName: string
	amountCents: number // the min or max amount in cents
	differenceCents: number // how far below min or above max the cart currently is
}

export const selectOrderTypeAmountAlert = createSelector(
	(state: { ticket: TicketState }) => state.ticket.selectedOrderType,
	(state: { ticket: TicketState }) => state.ticket.calculations,
	(state: { ticket: TicketState }) => state.ticket.items,
	(state: { ticket: TicketState }) => state.ticket.showOrderTypeMinWarning,
	(selectedOrderType, calculations, items, showMinWarning): OrderTypeAmountAlert | null => {
		if (!selectedOrderType || items.length === 0) return null
		if (!calculations?.totals) return null
		const total = calculations.totals.grandTotal
		const min = selectedOrderType.minOrderAmount
		const max = selectedOrderType.maxOrderAmount
		const name = selectedOrderType.name

		// Max check first — exceeding max always blocks, regardless of min state.
		if (max != null && total > max) {
			return {
				variant: 'above-max',
				orderTypeName: name,
				amountCents: max,
				differenceCents: total - max,
			}
		}

		if (min != null) {
			if (total < min) {
				return {
					variant: 'below-min',
					orderTypeName: name,
					amountCents: min,
					differenceCents: min - total,
				}
			}
			// Minimum is met — only show the success banner if the warning was previously triggered.
			if (showMinWarning) {
				return {
					variant: 'min-reached',
					orderTypeName: name,
					amountCents: min,
					differenceCents: 0,
				}
			}
		}

		return null
	},
)

export default ticketSlice.reducer
